import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2, Upload, PenLine, Send, Download, CheckCircle2, Clock, Plus, Trash2, MousePointerClick, Sparkles, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, uploadFile } from "../../lib/api";
import { EmployeePicker } from "../../components/os/EmployeePicker";
import { Badge } from "../../components/ui/badge";
import { useAuth } from "../../context/AuthContext";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

async function downloadReq(id, name) {
  try {
    const res = await api.get(`/people/sign/requests/${id}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = name || "documento.pdf"; a.click();
    window.URL.revokeObjectURL(url);
  } catch { toast.error("No se pudo descargar"); }
}

export default function Sign() {
  const { user } = useAuth();
  const canDelete = (r) => user && (r.requested_by === user.id || user.role === "admin");
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  // nueva solicitud
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(0.3);
  const [signer, setSigner] = useState(null);
  const [mark, setMark] = useState(null); // {x,y} normalizados
  const [signers, setSigners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [classification, setClassification] = useState(null);
  const pageWrapRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/people/sign/requests?scope=mine").catch(() => ({ data: { requests: [] } })),
      api.get("/people/sign/requests?scope=sent").catch(() => ({ data: { requests: [] } })),
    ]).then(([m, s]) => { setMine(m.data.requests || []); setSent(s.data.requests || []); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setFileUrl(URL.createObjectURL(f)); setSigners([]); setMark(null); setClassification(null); setBusy(true);
    try {
      const up = await uploadFile(f, true); setUploaded(up); toast.success("PDF cargado");
      setClassifying(true);
      try {
        const { data } = await api.post("/people/sign/classify", { document_path: up.path, filename: up.filename || f.name });
        setClassification(data.classification);
        toast.success("Documento clasificado con IA");
      } catch { toast.info("El enrutamiento con IA no está disponible; puedes continuar con la firma."); }
      finally { setClassifying(false); }
    }
    catch { toast.error("No se pudo subir el PDF"); setFile(null); setFileUrl(null); }
    finally { setBusy(false); }
  };

  const onPageClick = (e) => {
    const el = pageWrapRef.current?.querySelector(".react-pdf__Page");
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = Math.min(0.95, Math.max(0, (e.clientX - r.left) / r.width));
    const yTop = (e.clientY - r.top) / r.height;
    setMark({ x, y: Math.min(0.95, Math.max(0, 1 - yTop)) });
  };

  const addSigner = () => {
    if (!signer) return toast.error("Selecciona el firmante");
    if (!mark) return toast.error("Haz clic en el PDF para marcar dónde firmará");
    setSigners((prev) => [...prev, { signer_id: signer.id, signer_name: signer.name, page: Number(page), x: mark.x, y: mark.y, width: Number(width) }]);
    setSigner(null); setMark(null);
  };

  const create = async () => {
    if (!uploaded) return toast.error("Sube un PDF");
    const list = signers.length ? signers : (signer && mark ? [{ signer_id: signer.id, page: Number(page), x: mark.x, y: mark.y, width: Number(width) }] : []);
    if (!list.length) return toast.error("Agrega al menos un firmante con su posición");
    setBusy(true);
    try {
      await api.post("/people/sign/requests", { document_path: uploaded.path, filename: uploaded.filename || file?.name, classification, signers: list.map((s) => ({ signer_id: s.signer_id, page: s.page, x: s.x, y: s.y, width: s.width })) });
      toast.success("Solicitud de firma enviada");
      setFile(null); setFileUrl(null); setUploaded(null); setSigners([]); setSigner(null); setMark(null); setPage(1); setClassification(null); setTab("sent"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const doSign = async (id) => {
    setBusy(true);
    try { await api.post(`/people/sign/requests/${id}/sign`); toast.success("Documento firmado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al firmar"); }
    finally { setBusy(false); }
  };

  const removeReq = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"? Esta acción no se puede deshacer.`)) return;
    setBusy(true);
    try {
      await api.delete(`/people/sign/requests/${id}`);
      setMine((p) => p.filter((r) => r.id !== id));
      setSent((p) => p.filter((r) => r.id !== id));
      toast.success("Documento eliminado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
    finally { setBusy(false); }
  };

  const St = ({ s }) => s === "firmado"
    ? <Badge className="bg-emerald-500/15 text-emerald-600 border-none gap-1"><CheckCircle2 className="w-3 h-3" /> Firmado</Badge>
    : <Badge className="bg-gold/15 text-gold border-none gap-1"><Clock className="w-3 h-3" /> Pendiente</Badge>;

  const SignerChips = ({ r }) => (
    <div className="flex flex-wrap gap-1 mt-1">
      {(r.signers || []).map((s, i) => (
        <span key={i} className={`text-[11px] px-1.5 py-0.5 rounded ${s.status === "firmado" ? "bg-emerald-500/10 text-emerald-600" : "bg-navy/5 text-navy/45"}`}>
          {i + 1}. {s.signer_name}{s.status === "firmado" ? " ✓" : ""}
        </span>
      ))}
    </div>
  );

  return (
    <OSLayout title="OMNIA Sign — Firma Electrónica">
      <div className="max-w-4xl space-y-6" data-testid="sign-page">
        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-2">
          {[["mine", "Para firmar"], ["sent", "Enviados"], ["new", "Nueva solicitud"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`sign-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`}>{l}
              {k === "mine" && mine.filter((r) => r.my_turn).length > 0 && <span className="ml-2 text-xs bg-gold text-navy rounded-full px-1.5">{mine.filter((r) => r.my_turn).length}</span>}
            </button>
          ))}
        </div>

        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "new" && (
              <div className="bg-white border border-navy/10 rounded-2xl p-6 space-y-4" data-testid="sign-new">
                <label className="btn btn-ghost cursor-pointer gap-2 w-fit" data-testid="sign-upload">
                  {busy && !uploaded ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploaded ? "PDF cargado ✓" : "Subir PDF"}
                  <input type="file" accept=".pdf" className="hidden" onChange={onFile} />
                </label>

                {classifying && <div className="flex items-center gap-2 text-sm text-violet" data-testid="sign-classifying"><Loader2 className="w-4 h-4 animate-spin" /> Clasificando documento con IA…</div>}
                {classification && (
                  <div className="bg-violet/5 border border-violet/20 rounded-xl p-4" data-testid="sign-classification">
                    <div className="flex items-center gap-2 text-sm font-medium text-violet"><Sparkles className="w-4 h-4" /> Enrutamiento sugerido por IA</div>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      <span className="bg-white border border-violet/20 text-violet px-2 py-0.5 rounded-full capitalize">{(classification.tipo_documento || "").replace(/_/g, " ")}</span>
                      {classification.area_sugerida && <span className="bg-white border border-navy/15 text-navy/60 px-2 py-0.5 rounded-full">Área: {classification.area_sugerida}</span>}
                      <span className={`px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${classification.confidencialidad === "alta" ? "bg-destructive/10 text-destructive" : classification.confidencialidad === "media" ? "bg-gold/15 text-gold" : "bg-emerald-500/10 text-emerald-600"}`}><ShieldAlert className="w-3 h-3" /> Confid. {classification.confidencialidad}</span>
                    </div>
                    {classification.resumen && <p className="text-xs text-navy/60 mt-2">{classification.resumen}</p>}
                    {classification.firmantes_sugeridos?.length > 0 && (
                      <div className="text-xs text-navy/60 mt-2">Firmantes sugeridos: <span className="text-navy/80">{classification.firmantes_sugeridos.join(", ")}</span></div>
                    )}
                    {classification.campos_clave?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">{classification.campos_clave.map((c, i) => <span key={i} className="text-[11px] bg-navy/5 text-navy/55 px-2 py-0.5 rounded-full">{c}</span>)}</div>
                    )}
                  </div>
                )}

                {fileUrl && (
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <div className="text-xs text-navy/50 mb-2 flex items-center gap-1"><MousePointerClick className="w-3.5 h-3.5" /> Haz clic sobre el PDF para marcar la firma</div>
                      <div ref={pageWrapRef} onClick={onPageClick} className="relative border border-navy/15 rounded-lg overflow-hidden cursor-crosshair inline-block" data-testid="sign-pdf-preview">
                        <Document file={fileUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={<div className="p-8 text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
                          <Page pageNumber={page} width={340} renderTextLayer={false} renderAnnotationLayer={false} />
                        </Document>
                        {mark && <div className="absolute w-16 h-8 border-2 border-violet bg-violet/20 rounded pointer-events-none" style={{ left: `${mark.x * 100}%`, bottom: `${mark.y * 100}%` }} />}
                      </div>
                      {numPages > 1 && (
                        <div className="flex items-center gap-2 mt-2 text-sm">
                          <button onClick={() => setPage((p) => Math.max(1, p - 1))} className="btn btn-ghost btn-xs">‹</button>
                          <span className="text-navy/50">Pág. {page} / {numPages}</span>
                          <button onClick={() => setPage((p) => Math.min(numPages, p + 1))} className="btn btn-ghost btn-xs">›</button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-navy/60 mb-1 block">Firmante</label>
                        <EmployeePicker testid="sign-signer" value={signer?.id || null} onChange={setSigner} placeholder="¿Quién debe firmar?" />
                      </div>
                      <div><label className="text-xs text-navy/60 mb-1 block">Tamaño de firma ({Math.round(width * 100)}%)</label>
                        <input type="range" min={0.15} max={0.5} step={0.05} value={width} onChange={(e) => setWidth(e.target.value)} className="w-full accent-violet" data-testid="sign-width" /></div>
                      <div className="text-xs text-navy/45">{mark ? `Posición: pág ${page}, x ${Math.round(mark.x * 100)}%, y ${Math.round(mark.y * 100)}%` : "Marca la posición en el PDF."}</div>
                      <button onClick={addSigner} className="btn btn-ghost gap-2 text-sm" data-testid="sign-add-signer"><Plus className="w-4 h-4" /> Agregar firmante</button>

                      {signers.length > 0 && (
                        <div className="space-y-1" data-testid="sign-signers-list">
                          <div className="text-xs text-navy/60">Firmantes (en orden):</div>
                          {signers.map((s, i) => (
                            <div key={i} className="flex items-center justify-between bg-navy/[0.03] rounded px-2 py-1 text-sm">
                              <span className="text-navy">{i + 1}. {s.signer_name} <span className="text-navy/40 text-xs">pág {s.page}</span></span>
                              <button onClick={() => setSigners((p) => p.filter((_, j) => j !== i))} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <button onClick={create} disabled={busy} className="btn btn-primary gap-2 w-full" data-testid="sign-create"><Send className="w-4 h-4" /> Enviar a firma</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === "mine" && (
              mine.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">No tienes documentos por firmar.</div> :
              <div className="grid gap-3">
                {mine.map((r) => (
                  <div key={r.id} className="bg-white border border-navy/10 rounded-2xl p-4 flex items-center gap-3" data-testid={`sign-mine-${r.id}`}>
                    <PenLine className="w-5 h-5 text-navy/30 shrink-0" />
                    <div className="min-w-0 flex-1"><div className="text-sm font-medium text-navy truncate">{r.filename}</div><div className="text-xs text-navy/45">Solicitado por {r.requested_by_name}</div><SignerChips r={r} /></div>
                    <St s={r.status} />
                    {r.my_turn
                      ? <button onClick={() => doSign(r.id)} disabled={busy} className="btn btn-primary text-sm" data-testid={`sign-do-${r.id}`}>Firmar</button>
                      : r.my_pending
                        ? <span className="text-xs text-navy/40">Esperando turno</span>
                        : <button onClick={() => downloadReq(r.id, `${r.status === "firmado" ? "firmado_" : ""}${r.filename}`)} className="btn btn-ghost text-sm gap-1"><Download className="w-4 h-4" /> Descargar</button>}
                    {canDelete(r) && (
                      <button onClick={() => removeReq(r.id, r.filename)} disabled={busy} title="Eliminar documento"
                        className="text-navy/30 hover:text-destructive transition-colors shrink-0" data-testid={`sign-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === "sent" && (
              sent.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">No has enviado documentos a firma.</div> :
              <div className="grid gap-3">
                {sent.map((r) => (
                  <div key={r.id} className="bg-white border border-navy/10 rounded-2xl p-4 flex items-center gap-3" data-testid={`sign-sent-${r.id}`}>
                    <PenLine className="w-5 h-5 text-navy/30 shrink-0" />
                    <div className="min-w-0 flex-1"><div className="text-sm font-medium text-navy truncate">{r.filename}</div><SignerChips r={r} /></div>
                    <St s={r.status} />
                    <button onClick={() => downloadReq(r.id, `${r.status === "firmado" ? "firmado_" : ""}${r.filename}`)} className="btn btn-ghost text-sm gap-1"><Download className="w-4 h-4" /> Descargar</button>
                    {canDelete(r) && (
                      <button onClick={() => removeReq(r.id, r.filename)} disabled={busy} title="Eliminar documento"
                        className="text-navy/30 hover:text-destructive transition-colors shrink-0" data-testid={`sign-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </OSLayout>
  );
}
